import {Badge, Group, Tooltip, createStyles} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React from 'react';

interface ModelLabelsProps {
  capabilities: ReadonlyArray<string>;
  category: string;
}

export default function ModelLabels({capabilities, category}: ModelLabelsProps): JSX.Element {
  return (
    <Group aria-label="Model labels" noWrap={true} role="group" spacing={6}>
      <ModelLabelBadge isCategory={true} label={category} />
      {capabilities.map((capability) => (
        <ModelLabelBadge key={capability} label={capability} />
      ))}
    </Group>
  );
}

interface ModelLabelBadgeProps {
  isCategory?: boolean;
  label: string;
}

function ModelLabelBadge({isCategory = false, label}: ModelLabelBadgeProps): JSX.Element {
  const {classes} = useStyles();
  const tooltip = getModelLabelTooltip(label);
  const tooltipDescriptionId = useId();

  return (
    <>
      <Tooltip label={tooltip} multiline={true} openDelay={300} width={260} withArrow={true}>
        <Badge
          aria-describedby={tooltipDescriptionId}
          aria-label={label}
          className={classes.badge}
          classNames={{inner: isCategory ? classes.categoryLabel : classes.capabilityLabel}}
          color="gray"
          radius="sm"
          role="note"
          size="sm"
          variant="light"
        >
          {label}
        </Badge>
      </Tooltip>
      <span className={classes.visuallyHidden} id={tooltipDescriptionId}>
        {tooltip}
      </span>
    </>
  );
}

const MODEL_LABEL_TOOLTIPS: Record<string, string> = {
  molecules: 'Predicts structures containing small molecules',
  msa: 'Can use a multiple sequence alignment (MSA) to inform predictions',
  multimers: 'Predicts structures containing multiple protein chains',
  'single chain': 'Predicts the structure of one protein chain at a time',
  'structure prediction': 'Predicts 3D structures for proteins and biomolecular complexes',
};

function getModelLabelTooltip(label: string): string {
  return MODEL_LABEL_TOOLTIPS[label] ?? 'Describes a model capability';
}

const useStyles = createStyles((theme) => ({
  badge: {
    backgroundColor: theme.colors.gray[0],
    color: theme.colors.gray[7],
    height: 'auto',
    lineHeight: 1.2,
  },
  capabilityLabel: {
    fontWeight: 500,
    textTransform: 'lowercase',
  },
  categoryLabel: {
    fontWeight: 600,
    textTransform: 'lowercase',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
}));
